import { Grid } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useDataTableContext } from "../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterType } from "../../../components/DataTable/DataTableFilters/DataTableFilters";
import EntryRightForm from "../../../components/EntryRightForm/EntryRightForm";
import EntryRightMultipleDelegeesForm from "../../../components/EntryRightMultipleDelegeesForm/EntryRightMultipleDelegeesForm";
import { Position } from "../../../components/LocationCard/locationCard.business";
import MultipleVehiclesForm from "../../../components/MultipleVehiclesForm/MultipleVehiclesForm";
import MultipleVehiclesUploadView from "../../../components/MultipleVehiclesForm/MultipleVehiclesUploadView/VehiclesUploadView";
import ParkingRightsForm from "../../../components/ParkingRightForm/ParkingRightsForm";
import ParkingRightMultipleDelegeesForm from "../../../components/ParkingRightMultipleDelegeesForm/ParkingRightMultipleDelegeesForm";
import VehicleForm from "../../../components/VehicleForm/VehicleForm";
import BatchRevokeParkingRightsWarning from "../../../components/Warnings/BatchRevokeParkingRightsWarning";
import BatchRevokeVehicleAssignmentWarning from "../../../components/Warnings/BatchRevokeVehicleAssignmentWarning";
import DeleteDelegeeWarning from "../../../components/Warnings/DeleteDelegeeWarning";
import DeleteEntryRightDelegeeWarning from "../../../components/Warnings/DeleteEntryRightDelegeeWarning";
import DeleteVehiclesAssignmentWarning from "../../../components/Warnings/DeleteVehiclesAssignmentWarning";
import RevokeEntryRightWarning from "../../../components/Warnings/RevokeEntryRightWarning";
import RevokeParkingRightsWarning from "../../../components/Warnings/RevokeParkingRightsWarning";
import RevokeVehicleAssignmentWarning from "../../../components/Warnings/RevokeVehicleAssignmentWarning";
import { FmpFilters } from "../../../constants/filtering.constants";
import useAppContext from "../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../context/hooks/useFleetManagerContext";
import useImportDelegationsContext from "../../../context/hooks/useImportDelegationsContext";
import useMyProductsContext from "../../../context/hooks/useMyProductsContext";
import ImportVehiclesContext from "../../../context/ImportVehiclesContext";
import { useOnBulkImportFinishedCallback } from "../../../hooks/useOnBulkImportFinishedCallback";
import { WithFiltersAndPaging } from "../../../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../../../models/ForFleetManager";
import { ContractType, ParkingProductType } from "../../../models/ParkingProductModel";
import { VehicleAssignmentProduct } from "../../../models/vehicle-assignments/VehicleAssignmentProduct";
import { convertFiltersToGetVehicleAssignmentsQuery } from "../../../services/parkingProducts.service";
import { useAppDispatch, useAppSelector } from "../../../store";
import * as entryRightsActions from "../../../store/actions/entryRights.actions";
import * as parkingProductsThunks from "../../../store/actions/parkingProducts.thunks";
import { variables } from "../../../theme/variables";
import ActionDrawer from "../../../ui/ActionDrawer/ActionDrawer";
import Section from "../../../ui/Section/Section";
import Spacer from "../../../ui/Spacer/Spacer";
import "./_myProducts.scss";
import LocationCards from "./LocationCards/LocationCards";
import { selectEnableDelegationsScheduling, selectEnableFleetVehicleScheduling, selectUseBulkVehicleAssignments, selectUseDirectVehicleAssignment, selectUseEntryRightsForFleetMembers, TabOption, useCurrentlySelectedParkingProduct } from "./ParkingRightsDataTable/common";
import EntryRightTable from "./ParkingRightsDataTable/EntryRightTable";
import FleetMembersTable from "./ParkingRightsDataTable/FleetMembersTable";
import TableSwitcher from "./ParkingRightsDataTable/TableSwitcher";
import VehiclesTable from "./ParkingRightsDataTable/VehiclesTable";

const { colors } = variables;

export interface MyProductsProps { }
const MyProducts = (_: MyProductsProps) => {
  const { appState } = useAppContext();
  const {
    myProductsState,
    resetContext: resetMyProductsContext,
    setSorting
  } = useMyProductsContext();
  const dispatch = useAppDispatch();
  const { resetContext, resetFilters, resetPageNumber, resetPageSize } = useDataTableContext();

  const {
    fleetManagerState,
    toggleAddDelegee,
    toggleAssignVehicle,
    toggleEditVehicleAssignment,
    toggleEditDelegee,
    toggleMultipleVehicles,
    toggleDeleteDelegeeWarning,
    toggleBatchRevokeParkingRightsWarning,
    toggleBatchRevokeVehicleAssignmentsWarning,
    toggleRevokeParkingRightsWarning,
    toggleRevokeVehicleAssignmentsWarning,
    toggleDeleteVehicleAssignmentsWarning,
    toggleEntryRightInvitationForm,
    setSelectedParkingRightIds,
    resetContext: resetFleetManagerContext,
  } = useFleetManagerContext();

  const toggleRevokeEntryRightsWarning = () => dispatch(entryRightsActions.toggleShowRevokeEntryRightsAction());
  const toggleDeleteEntryRightsDelegeesWarning = () => dispatch(entryRightsActions.toggleShowDeleteEntryRightsDelegeesAction());

  const { importDelegationsState } = useImportDelegationsContext();

  const [tab, setTab] = useState(TabOption.FleetMembers);

  const useDirectVehicleAssignment = useAppSelector(selectUseDirectVehicleAssignment);
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);

  const selectedLocatedProduct = useCurrentlySelectedParkingProduct();
  const showRevokeEntryRightsWarning = useAppSelector((state) => state.entryRights.showRevokePedestriansWarning);
  const showDeleteEntryRightsDelegeesWarning = useAppSelector((state) => state.entryRights.showDeleteEntryRightDelegeesWarning);
  const useEntryRightsForFleetMembers = useAppSelector(selectUseEntryRightsForFleetMembers);
  const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling);
  const myProductsTotalRecords = useAppSelector((state) => {
      return {
          pedestrian: state.entryRights.myProducts.pedestrian.totalRecords,
          parking: state.entryRights.myProducts.parking.totalRecords,
      };
  }, shallowEqual);

  useEffect(() => {
    // prefetch the total records so the TableSwitch shows some number before you click on it
    if (enableDelegationsScheduling && selectedLocatedProduct?.parkingProducts.some(p => p.productType === ParkingProductType.PedestrianAccess)) {
      dispatch(entryRightsActions.getTotalRecords({
        seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
        placeId: myProductsState.selectedLocation.placeId,
        language: appState.selectedLanguage,
        productType: ParkingProductType.PedestrianAccess
      }));
    }
  }, [selectedLocatedProduct]);

  const onTabSelected = (newTab: TabOption): void => {
    if (tab !== newTab) {
      resetPageNumber();
      resetPageSize();
      setSelectedParkingRightIds([]);

      let sorting = "";
      switch (newTab) {
        case TabOption.FleetMembers:
          sorting = "invitationDate";
          break;
        case TabOption.Vehicles:
          sorting = "assignedDate";
          break;
      }

      resetContext();
      setSorting(sorting, true);
    }
    setTab(newTab);
  };

  const { t } = useTranslation(["myProducts", "globals", "dataTable"]);

  const hideDva = !useDirectVehicleAssignment;
  const hasUnlimitedEntryRights = useAppSelector(s => s.parkingProducts.hasUnlimitedEntryRights);
  const totalVehicleAssignments = useAppSelector(s => s.parkingProducts.vehiclesMyProducts.totalRecords);
  const hidePedestrianRightsOption = !selectedLocatedProduct?.totalPedestrianRights;

  const switcher = (
    <TableSwitcher
      selected={tab}
      onTabSelected={onTabSelected}
      options={[
        {
          value: TabOption.FleetMembers,
          label: t("tableSwitcher:parkingRights", {
            count: enableDelegationsScheduling ? myProductsTotalRecords.parking :  selectedLocatedProduct?.totalDelegatedParkingRights ?? 0,
          }),
          hide: hasUnlimitedEntryRights,
        },
        {
          value: TabOption.PedestrianRights,
          label: t("tableSwitcher:pedestrianRights", { count: enableDelegationsScheduling ? myProductsTotalRecords.pedestrian : selectedLocatedProduct?.totalDelegatedPedestrianRights ?? 0 }),
          hide: hidePedestrianRightsOption,
        },
        {
          value: TabOption.Vehicles,
          label: t("tableSwitcher:vehicles", {
            count: enableFleetVehicleScheduling ? totalVehicleAssignments ?? 0:
             selectedLocatedProduct?.totalAssignedParkingRights ?? 0,
          }),
          hide: hideDva,
        },
      ]}
    />
  );

  const getMetaTitle = () =>
    fleetManagerState.showDelegeeDetails ? t("addDelegee") : t("myProducts");

  useEffect(() => {
    if (useBulkVehicleAssignments) {
      setTab(TabOption.Vehicles);
    }
  }, [useBulkVehicleAssignments]);

  useEffect(() => {
    return () => {
      resetMyProductsContext();
      resetFleetManagerContext();
      resetContext();
    };
    // eslint-disable-next-line
  }, []);

  // counter prefetch
  useEffect(() => {
    if (!enableFleetVehicleScheduling || myProductsState.selectedLocation.placeId === 0 || tab === TabOption.Vehicles) {
      return;
    }

    const thunkResult = dispatch(parkingProductsThunks.getVehiclesThunk({
      language: appState.selectedLanguage,
      seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
      placeId: myProductsState.selectedLocation.placeId,
      pageSize: 1,
      page: 1,
      countersOnly: true,
    }));

    return () => thunkResult.abort();
  }, [enableFleetVehicleScheduling, myProductsState.selectedLocation.placeId, tab, appState.user.seasonTicketOwnerCrmId]);

  const onDelegeeFormClose = () => {
    if (fleetManagerState.isEditDelegeeMode) {
      toggleEditDelegee("");
    } else {
      toggleAddDelegee();
    }
  };

  const onVehicleFormClose = () => {
    if (fleetManagerState.isEditVehicleAssignmentMode) {
      toggleEditVehicleAssignment();
    } else {
      toggleAssignVehicle();
    }
  };

  const onMultipleVehiclesClose = () => {
    toggleMultipleVehicles();
  };

  const onPedestrianInvitationFormClose = () => {
    toggleEntryRightInvitationForm();
  };

  const [position, setPosition] = useState<Position>({
    left: 0,
    right: 0,
    width: 0,
  });

  const onLocationCardSelect = (pos?: Position) => {
    resetFilters();
    setSelectedParkingRightIds([]);

    setPosition({
      ...position,
      left: pos?.left as number,
      right: pos?.right as number,
      width: pos?.width as number,
    });
  };


  const onRefresh = (
    vehiclesToExclude?: VehicleAssignmentProduct[],
    excludeByVehicleAssignmentId?: boolean
  ) => {
      // Dont fetch the data again from the backend because of async process but do an optimistic update.
      dispatch(
          parkingProductsThunks.refreshVehiclesMyProducts((q) => ({
              ...q,
              vehiclesToExclude,
              excludeByVehicleAssignmentId,
          }))
      );
  };

  useOnBulkImportFinishedCallback(onRefresh);

  const getSelectedEntryRightDelegees = (): string[] =>
    fleetManagerState.selectedRowKeys?.length > 0
      ? fleetManagerState.selectedRowKeys
      : [fleetManagerState.selectedDelegeeId];

  const getDeleteDelegeeWarningText = (parkingRightsCount: number): string =>
    t("deleteDelegeeWarning.warning", { count: parkingRightsCount });

  const getDeleteDelegeeWarningDetails = (parkingRightsCount: number): string =>
    t("deleteDelegeeWarning.details", { count: parkingRightsCount });

  const getRevocationEmailText = (): string =>
    t("deleteDelegeeWarning.sendRevocationEmail");

  const selectedBulkParkingProductIsGoBusiness = importDelegationsState.checkParkingRights?.length > 0 &&
    importDelegationsState.checkParkingRights.every(cpr => cpr.right?.contractType === ContractType.GoBusiness);

  const showEntryRightMultipleDelegeesform = selectedBulkParkingProductIsGoBusiness;

  const selectedBulkParkingProductIsSeasonTicket = importDelegationsState.checkParkingRights?.length > 0 &&
    importDelegationsState.checkParkingRights.every(cpr => cpr.right?.contractType === ContractType.SeasonTicket);

  const showParkingProductMultipleDelegeesform = selectedBulkParkingProductIsSeasonTicket;

  const revokeProductType =
    selectedLocatedProduct?.contractType === ContractType.GoBusiness ||
    (useEntryRightsForFleetMembers && tab === TabOption.FleetMembers)
      ? 'parking' as const
      : 'pedestrian' as const;

  return (
    <React.Fragment>
      <Helmet>
        <title>{`Q-Park | ${getMetaTitle()}`}</title>
      </Helmet>
      <React.Fragment>
        <ActionDrawer
          open={fleetManagerState.showDelegeeDetails && !fleetManagerState.dragAndDropView}
          onClose={onDelegeeFormClose}
          className="delegee-form-drawer"
        >
          {enableDelegationsScheduling ? (
            <EntryRightForm
              onClose={onDelegeeFormClose}
              refreshHandler={onRefresh}
              delegationMode="parkingRights"
            />
          ) : (
            <ParkingRightsForm
              onClose={onDelegeeFormClose}
              refreshHandler={onRefresh}
            />
          )}
        </ActionDrawer>
        {fleetManagerState.showDelegeeDetails && showParkingProductMultipleDelegeesform &&
          <ParkingRightMultipleDelegeesForm onClose={onDelegeeFormClose} translationContextName="parkingRightsForm" />}
        {fleetManagerState.showDelegeeDetails && showEntryRightMultipleDelegeesform &&
          <EntryRightMultipleDelegeesForm onClose={onDelegeeFormClose} translationContextName="parkingRightsForm" />}
      </React.Fragment>
      <ActionDrawer
        open={fleetManagerState.showDeleteDelegeeWarning}
        onClose={toggleDeleteDelegeeWarning}
      >
        <DeleteDelegeeWarning
          getWarningText={getDeleteDelegeeWarningText}
          getWarningDetails={getDeleteDelegeeWarningDetails}
          getRevocationEmailText={getRevocationEmailText}
          onRefresh={onRefresh}
        />
      </ActionDrawer>
      <ActionDrawer
        open={fleetManagerState.showBatchRevokeParkingRightsWarning}
        onClose={toggleBatchRevokeParkingRightsWarning}
      >
        <BatchRevokeParkingRightsWarning
          onRefresh={onRefresh}
          selectedParkingRightIds={fleetManagerState.selectedRowKeys}
        />
      </ActionDrawer>
      <ActionDrawer
        open={fleetManagerState.showBatchRevokeParkingVehicleAssignment}
        onClose={toggleBatchRevokeVehicleAssignmentsWarning}
      >
        <BatchRevokeVehicleAssignmentWarning
          onRefresh={onRefresh}
          selectedVehicleAssignmentKeys={fleetManagerState.selectedRowKeys}
        />
      </ActionDrawer>
      <ActionDrawer
        open={fleetManagerState.showRevokeParkingRightsWarning}
        onClose={toggleRevokeParkingRightsWarning}
      >
        <RevokeParkingRightsWarning
          onRefresh={onRefresh}
          selectedParkingRightIds={[fleetManagerState.selectedDelegeeRowKey]}
        />
      </ActionDrawer>
      <ActionDrawer
        open={showRevokeEntryRightsWarning}
        onClose={toggleRevokeEntryRightsWarning}
      >
        <RevokeEntryRightWarning
          revokeProductType={revokeProductType}
        />
      </ActionDrawer>
      <ActionDrawer
        open={fleetManagerState.showRevokeParkingVehicleAssignment}
        onClose={toggleRevokeVehicleAssignmentsWarning}
      >
        <RevokeVehicleAssignmentWarning
          onRefresh={onRefresh}
        />
      </ActionDrawer>
      <ActionDrawer
        open={fleetManagerState.showDeleteVehiclesAssignment}
        onClose={toggleDeleteVehicleAssignmentsWarning}
      >
        <DeleteVehiclesAssignmentWarning
          onRefresh={onRefresh}
          selectedVehicleAssignmentKeys={fleetManagerState.selectedRowKeys}
        />
      </ActionDrawer>
      <ActionDrawer
        open={showDeleteEntryRightsDelegeesWarning}
        onClose={toggleDeleteEntryRightsDelegeesWarning}
      >
        <DeleteEntryRightDelegeeWarning
          selectedPedestrianIds={getSelectedEntryRightDelegees()}
        />
      </ActionDrawer>

      <ImportVehiclesContext>
        <ActionDrawer
          open={fleetManagerState.showWhitelistVehicles && !fleetManagerState.vehicleDragAndDropView}
          onClose={onMultipleVehiclesClose}>
          <MultipleVehiclesForm onClose={onMultipleVehiclesClose} />
        </ActionDrawer>
        <ActionDrawer open={fleetManagerState.vehicleDragAndDropView}>
          <MultipleVehiclesUploadView onRefresh={onRefresh} />
        </ActionDrawer>
        <ActionDrawer
          open={fleetManagerState.showAssignVehicle}
          onClose={onVehicleFormClose}>
          <VehicleForm onClose={onVehicleFormClose} />
        </ActionDrawer>
        <ActionDrawer
          open={fleetManagerState.showPedestrianInvitationForm}
          onClose={onPedestrianInvitationFormClose}>
          <EntryRightForm onClose={onPedestrianInvitationFormClose} delegationMode="pedestrian" />
        </ActionDrawer>
        {fleetManagerState.showPedestrianInvitationForm &&
          <EntryRightMultipleDelegeesForm onClose={onPedestrianInvitationFormClose} translationContextName="pedestrianInvitationForm" />}
      </ImportVehiclesContext>
      <Spacer />
      <LocationCards onCardSelected={onLocationCardSelect} />
      <Spacer size="sm" />
      {myProductsState.selectedLocation.placeId !== 0 && (
        <Grid container direction="row" alignItems="center">
          <Section
            backgroundColor={colors.white}
            withArrow
            left={position.left! - position.width! / 2.5}
          >
            {selectedLocatedProduct?.contractType != ContractType.GoBusiness &&
              <>
                {tab === TabOption.FleetMembers &&
                  (useEntryRightsForFleetMembers || enableDelegationsScheduling
                    ? <EntryRightTable
                      onRevokeClicked={toggleRevokeEntryRightsWarning}
                      onDeleteClicked={() => { }}
                      subHeading={switcher}
                      parkingProductType={ParkingProductType.ParkingRights}
                      translationKey="parkingRights"
                    />
                    : <FleetMembersTable
                      selectedParkingProduct={selectedLocatedProduct?.parkingProducts}
                      onRevokeClicked={toggleBatchRevokeParkingRightsWarning}
                      subHeading={switcher} />
                  )
                }
                {tab === TabOption.Vehicles &&
                  <VehiclesTable
                    onRevokeClicked={toggleBatchRevokeVehicleAssignmentsWarning}
                    onDeleteClicked={toggleDeleteVehicleAssignmentsWarning}
                    subHeading={switcher} />
                }
                {tab === TabOption.PedestrianRights &&
                  <EntryRightTable
                    onRevokeClicked={toggleRevokeEntryRightsWarning}
                    onDeleteClicked={() => { }}
                    subHeading={switcher}
                    parkingProductType={ParkingProductType.PedestrianAccess}
                    translationKey="pedestrianRights"
                  />
                }
              </>}
            {selectedLocatedProduct?.contractType == ContractType.GoBusiness &&
              <EntryRightTable
                onRevokeClicked={toggleRevokeEntryRightsWarning}
                onDeleteClicked={() => { }}
                parkingProductType={ParkingProductType.ParkingRights}
                translationKey="parkingRights"
              />
            }
          </Section>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default MyProducts;
